import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import type { ChangeHandler } from '../types';
import type { FreeBetsFromBackend } from 'app/api/services/freebets/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { selectFreeBetsData } from 'app/lib/account/selectors';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { type ChangeEntryAmount, changeEntryAmount } from '../../../actions/entries';
import useEntryDisabledTitles from '../../../hooks/useEntryDisabledTitles';
import { selectAllUsedFreeBets } from '../../../selectors/entries';
import { selectSingleBetsInfo } from '../../../selectors/stakeTypes/single';
import BarContent from './BarContent';

import dictionary from '../FooterBar.dictionary.json';

// ---

type EntryFooterBarProps = {
  betId: number;
};

type StoreProps = {
  loggedIn: boolean;
  freebets: FreeBetsFromBackend;
  maxValue: number;
  usedFreeBets: Set<number>;
  fullName?: string;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeEntryAmount>;
};

const EntryFooterBar: FC<EntryFooterBarProps & StoreProps & DispatchProp> = ({
  betId,
  freebets,
  usedFreeBets,
  loggedIn,
  maxValue,
  dispatch,
}) => {
  const { t } = useTranslation();
  const disabledTitles = useEntryDisabledTitles(betId);

  const freeBetItems = useMemo(() => {
    return freebets.map((freebet) => {
      const isAmountMoreThanMaxWinning = freebet.amount > maxValue;
      let disabledTitle = '';
      if (isAmountMoreThanMaxWinning) {
        disabledTitle = t(dictionary.notUseInThisMatch);
      }
      if (usedFreeBets.has(freebet.id)) {
        disabledTitle = t(dictionary.freebetAlreadyInUse);
      }
      return ({
        ...freebet,
        disabledTitle: disabledTitles?.freeBets || disabledTitle,
        disabled: usedFreeBets.has(freebet.id)
          || isAmountMoreThanMaxWinning
          || Boolean(disabledTitles?.freeBets),
      });
    });
  }, [usedFreeBets, freebets, maxValue, loggedIn]);

  const changeEntryHandler: ChangeHandler = useCallback(({ value, account }) => {
    if (!account) {
      console.error('BetSlip -> EntryFooterBar -> `account` not found');
      return;
    }

    dispatch(changeEntryAmount(betId, value, account));
  }, [betId]);

  return (
    <BarContent betId={betId}
                freeBetItems={freeBetItems}
                onChange={changeEntryHandler} />
  );
};

const mapStateToProps = (state: ReduxState, { betId }: EntryFooterBarProps): StoreProps => {
  const betInfo = selectSingleBetsInfo(state).get(betId);

  return {
    loggedIn: selectIsLoggedIn(state),
    freebets: selectFreeBetsData(state) || [],
    usedFreeBets: selectAllUsedFreeBets(state),
    maxValue: betInfo?.limits.max || 0,
  };
};

export default connect(mapStateToProps)(EntryFooterBar);
